import { SyntheticEvent, useState, useEffect } from 'react';
import { OkvedInfoDto } from '@sr/dto';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { useOkvedDropdown } from './hook';
import { useField } from 'formik';
import { FILTER_CHIP_MAX_WIDTH } from 'shared/ui/collection-multiple-autocomplete-filter';

interface OkvedPickerFieldFilterProps {
  name: string;
  label: string;
  customSize?: number;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  [key: string]: any;
}

export const OkvedPickerFieldFilter = (props: OkvedPickerFieldFilterProps) => {
  const { label, name, customSize = 1, size = 'medium', fullWidth = true, ...otherProps } = props;
  
  const [field, meta, helpers] = useField(name);
  const { items: list, isLoading: loading } = useOkvedDropdown();
  const [selectedValues, setSelectedValues] = useState<OkvedInfoDto[]>([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (list.length > 0) {
      try {
        if (!field.value || 
            (Array.isArray(field.value) && field.value.length === 0) ||
            field.value === '') {
          setSelectedValues([]);
          return;
        }
        
        const valuesArray = (Array.isArray(field.value) ? field.value : [field.value]).map((value) => String(value));
        const selected = list.filter(item => 
          item && item.id && valuesArray.includes(String(item.id))
        );
        setSelectedValues(selected);
      } catch (error) {
        console.error('Error parsing field value:', error);
        setSelectedValues([]);
      }
    } else {
      setSelectedValues([]);
    }
  }, [list, field.value]);

  const handleChange = (
    _event: SyntheticEvent<Element, Event>, 
    newValue: OkvedInfoDto[]
  ) => {
    setSelectedValues(newValue);
    
    if (newValue.length === 0) {
      helpers.setValue(undefined);
    } else {
      const newIds = newValue.map(x => x.id);
      helpers.setValue(newIds);
    }
    helpers.setTouched(true);
  };

  const handleInputChange = (
    _event: SyntheticEvent<Element, Event>,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);
  };

  const renderTags = (value: OkvedInfoDto[], getTagProps: any) => {
    const displayedValues = value.slice(0, customSize);
    const additionalCount = value.length > customSize ? value.length - customSize : 0;
    
    return [
      ...displayedValues.map((option, index) => (
        (() => {
          const { onDelete: _onDelete, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={option.id}
              label={`${option.code} - ${option.name}`}
              size="small"
              variant="outlined"
              sx={{
                maxWidth: FILTER_CHIP_MAX_WIDTH,
                '& .MuiChip-label': {
                  maxWidth: FILTER_CHIP_MAX_WIDTH,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                },
              }}
            />
          );
        })()
      )),
      ...(additionalCount > 0 
        ? [<Chip key="more" size="small" variant="outlined" label={`+${additionalCount}`} />] 
        : []
      )
    ];
  };

  return (
    <Autocomplete
      multiple
      disableClearable
      options={list}
      value={selectedValues}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      loading={loading}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => `${option.code} - ${option.name}`}
      renderTags={renderTags}
      renderInput={(params) => (
        <TextField
          {...params}
          {...otherProps}
          label={label}
          placeholder="Введите код или название ОКВЭД..."
          error={meta.touched && !!meta.error}
          helperText={meta.touched && meta.error}
          size={size}
          fullWidth={fullWidth}
        />
      )}
      sx={{
        '& .MuiAutocomplete-tag': {
          maxWidth: FILTER_CHIP_MAX_WIDTH,
          minWidth: 0,
        },
        '& .MuiInputBase-root': customSize ? {
          flexWrap: 'nowrap !important',
          overflow: 'auto'
        } : {},
        ...otherProps.sx
      }}
    />
  );
};
